import {
  SubscriptionPlans,
  SubscriptionStatus,
  type Subscription,
} from "@prisma/client";
import { isAxiosError } from "axios";

export const SubscriptionChangeBlockReason = {
  stripe: "stripe",
  tempOrNonBillable: "temp_or_non_billable",
  invalidStatus: "invalid_status",
  paddleUnavailable: "paddle_unavailable",
  paymentMethodRequired: "payment_method_required",
  renewBeforeChange: "renew_before_change",
} as const;

export type TSubscriptionChangeBlockReason =
  (typeof SubscriptionChangeBlockReason)[keyof typeof SubscriptionChangeBlockReason];

export const SUBSCRIPTION_CHANGE_MESSAGES: Record<
  TSubscriptionChangeBlockReason,
  string
> = {
  [SubscriptionChangeBlockReason.stripe]:
    "Manage this plan in the Stripe billing portal.",
  [SubscriptionChangeBlockReason.tempOrNonBillable]:
    "This plan isn't linked to a billable subscription yet. Set up billing to choose a plan — you'll go through the same checkout as a new subscriber.",
  [SubscriptionChangeBlockReason.invalidStatus]:
    "You can't change your plan while your subscription is paused, past due, or canceled.",
  [SubscriptionChangeBlockReason.paddleUnavailable]:
    "We couldn't load your billing subscription. Try again, or update your payment method.",
  [SubscriptionChangeBlockReason.paymentMethodRequired]:
    "Add a payment method, then try changing your plan again.",
  [SubscriptionChangeBlockReason.renewBeforeChange]:
    "Your subscription is set to cancel at the end of the billing period. Keep your subscription first (Billing → Keep Subscription), then change your plan.",
};

const CHANGEABLE_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.active,
  SubscriptionStatus.trialing,
];

export type TSubscriptionChangeEligibilityInput = Pick<
  Subscription,
  "provider" | "subscriptionId" | "status" | "plan" | "canceledAt"
>;

export type TSubscriptionChangeEligibilityOptions = {
  /** True when a live Paddle fetch finished and returned nothing. */
  paddleLoadFailed?: boolean;
  requireNotCanceling?: boolean;
};

export const isLivePaddleSubscriptionId = (subscriptionId: string): boolean =>
  !subscriptionId.includes("temp");

export const getSubscriptionChangeMessage = (
  reason: TSubscriptionChangeBlockReason
): string => SUBSCRIPTION_CHANGE_MESSAGES[reason];

/**
 * Returns a block reason when a paid user cannot self-serve change plan /
 * interval via Paddle. Free users return null (they use checkout instead).
 */
export const getSubscriptionChangeBlockReason = (
  subscription: TSubscriptionChangeEligibilityInput | null | undefined,
  options: TSubscriptionChangeEligibilityOptions = {}
): TSubscriptionChangeBlockReason | null => {
  if (!subscription || subscription.plan === SubscriptionPlans.free) {
    return null;
  }

  if (!isLivePaddleSubscriptionId(subscription.subscriptionId)) {
    return SubscriptionChangeBlockReason.tempOrNonBillable;
  }

  if (!CHANGEABLE_STATUSES.includes(subscription.status)) {
    return SubscriptionChangeBlockReason.invalidStatus;
  }

  if (options.requireNotCanceling && subscription.canceledAt) {
    return SubscriptionChangeBlockReason.renewBeforeChange;
  }

  if (options.paddleLoadFailed) {
    return SubscriptionChangeBlockReason.paddleUnavailable;
  }

  return null;
};

/** True when a paid user can use Paddle preview/patch to change plan or interval. */
export const canSelfServeChangeSubscription = (
  subscription: TSubscriptionChangeEligibilityInput | null | undefined,
  options: TSubscriptionChangeEligibilityOptions = {}
): boolean => {
  if (!subscription || subscription.plan === SubscriptionPlans.free) {
    return false;
  }

  return getSubscriptionChangeBlockReason(subscription, options) === null;
};

/**
 * Paid-but-non-billable accounts (temp/comp Pro, missing Paddle sub) should
 * open a brand-new checkout instead of the in-place upgrade path.
 */
export const shouldStartFreshBillingCheckout = (
  subscription: TSubscriptionChangeEligibilityInput | null | undefined,
  options: TSubscriptionChangeEligibilityOptions = {}
): boolean => {
  if (!subscription || subscription.plan === SubscriptionPlans.free) {
    return false;
  }

  return (
    getSubscriptionChangeBlockReason(subscription, options) ===
    SubscriptionChangeBlockReason.tempOrNonBillable
  );
};

const PAYMENT_METHOD_ERROR_PATTERN =
  /payment[_\s-]?method|no payment method|payment method required|unable to charge|card/i;

const NOT_FOUND_ERROR_PATTERN =
  /not[_\s-]?found|does not exist|subscription.*invalid|404/i;

const extractPaddleErrorText = (err: unknown): string => {
  if (!isAxiosError(err)) {
    if (err instanceof Error) {
      return err.message;
    }
    return String(err ?? "");
  }

  const data = err.response?.data as
    | {
        error?: { detail?: string; code?: string; message?: string };
        message?: string;
      }
    | undefined;

  const parts = [
    data?.error?.detail,
    data?.error?.code,
    data?.error?.message,
    data?.message,
    err.message,
  ].filter(Boolean);

  return parts.join(" ");
};

/**
 * Map a Paddle axios (or generic) failure to a user-facing block reason.
 */
export const mapPaddleSubscriptionChangeError = (
  err: unknown
): TSubscriptionChangeBlockReason => {
  const text = extractPaddleErrorText(err);

  if (PAYMENT_METHOD_ERROR_PATTERN.test(text)) {
    return SubscriptionChangeBlockReason.paymentMethodRequired;
  }

  if (
    (isAxiosError(err) && err.response?.status === 404) ||
    NOT_FOUND_ERROR_PATTERN.test(text)
  ) {
    return SubscriptionChangeBlockReason.tempOrNonBillable;
  }

  return SubscriptionChangeBlockReason.paddleUnavailable;
};

export const GENERIC_SUBSCRIPTION_CHANGE_ERROR =
  "Unable to change your subscription. Please try again or contact support.";

export const PENDING_CHANGE_BEFORE_CANCELLATION_MESSAGE =
  "Undo your scheduled downgrade before canceling your subscription.";

export const canCancelSubscription = (
  subscription: Pick<Subscription, "pendingPlan"> | null | undefined
): boolean => !subscription?.pendingPlan;
